import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '../../../theme/colors';

export interface CopilotInput {
  text: string;
  files: File[];
}

interface CopilotInputBarProps {
  onSend: (input: CopilotInput) => void;
  onMarkComplete: () => void;
  isCompleted: boolean;
  isSending?: boolean;
  isMarkingComplete?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Hide the mark complete button (for mode-based panels where
   * completion is handled by the mode-specific content).
   */
  hideMarkComplete?: boolean;
}

export const CopilotInputBar: React.FC<CopilotInputBarProps> = ({
  onSend,
  onMarkComplete,
  isCompleted,
  isSending = false,
  isMarkingComplete = false,
  placeholder = 'Ask for help, add context, or upload files...',
  disabled = false,
  hideMarkComplete = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = inputValue.trim();
    if ((trimmed || attachedFiles.length > 0) && !isSending && !disabled) {
      onSend({ text: trimmed, files: attachedFiles });
      setInputValue('');
      setAttachedFiles([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setAttachedFiles((prev) => [...prev, ...Array.from(files)]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const canSubmit =
    (inputValue.trim() || attachedFiles.length > 0) && !isSending && !disabled;

  return (
    <Box
      sx={{
        borderTop: `1px solid ${colors.gray[200]}`,
        bgcolor: colors.gray[50],
        p: 1,
      }}
    >
      {/* Attached files preview */}
      {attachedFiles.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            mb: 1,
          }}
        >
          {attachedFiles.map((file, idx) => (
            <Chip
              key={`${file.name}-${idx}`}
              label={file.name}
              size="small"
              onDelete={() => handleRemoveFile(idx)}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{
                bgcolor: colors.gray[100],
                '& .MuiChip-label': { fontSize: '0.75rem' },
              }}
            />
          ))}
        </Box>
      )}

      {/* Input row */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
        }}
      >
        {/* File attachment button */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.csv,.xlsx,.xls"
        />
        <Tooltip title="Attach files">
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isSending}
            size="small"
            sx={{
              color: colors.gray[500],
              '&:hover': { color: colors.blue[600] },
            }}
          >
            <AttachFileIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        {/* Text input */}
        <TextField
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          multiline
          maxRows={4}
          fullWidth
          size="small"
          disabled={disabled || isSending}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: colors.gray[50],
              borderRadius: 3,
              fontSize: '0.875rem',
              '& fieldset': {
                borderColor: colors.gray[300],
              },
              '&:hover fieldset': {
                borderColor: colors.gray[400],
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.blue[500],
              },
            },
          }}
        />

        {/* Send button */}
        <Tooltip title="Send">
          <span>
            <IconButton
              type="submit"
              disabled={!canSubmit}
              size="small"
              sx={{
                bgcolor: canSubmit ? colors.blue[600] : colors.gray[200],
                color: canSubmit ? 'white' : colors.gray[400],
                borderRadius: 2,
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: canSubmit ? colors.blue[700] : colors.gray[200],
                },
                '&.Mui-disabled': {
                  bgcolor: colors.gray[200],
                  color: colors.gray[400],
                },
              }}
            >
              {isSending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SendIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {/* Mark complete button - hidden when mode handles completion */}
        {!hideMarkComplete && (
          <Tooltip title={isCompleted ? 'Completed' : 'Mark complete'}>
            <span>
              <IconButton
                onClick={onMarkComplete}
                disabled={isMarkingComplete || disabled}
                size="small"
                sx={{
                  bgcolor: isCompleted ? colors.green[600] : colors.green[50],
                  color: isCompleted ? 'white' : colors.green[600],
                  borderRadius: 2,
                  width: 32,
                  height: 32,
                  border: `1px solid ${colors.green[600]}`,
                  '&:hover': {
                    bgcolor: isCompleted ? colors.green[700] : colors.green[100],
                  },
                }}
              >
                {isMarkingComplete ? (
                  <CircularProgress size={18} color="inherit" />
                ) : isCompleted ? (
                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                ) : (
                  <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default CopilotInputBar;
